/**
 * Meta de auto-preenchimento dos palpites do bolão.
 *
 * Quando o participante usa "Preencher tudo (sobrescreve)" ou
 * "Preencher só os vazios" no formulário público, o tipo é registrado
 * aqui e enviado ao Google Sheets como um "palpite" reservado
 * (jogoId = "__meta_auto__"), encaixando no formato existente da API
 * SEM precisar atualizar o Apps Script. O admin decodifica e mostra
 * um badge ao lado do nome.
 *
 * Codificação no Placar reservado (gravado no envio):
 *   - mandante = 1  → 'sobrescreve'
 *   - mandante = 2  → 'somente-vazios'
 *   - visitante = quantidade de jogos MANUAIS:
 *       · sobrescreve: jogos editados manualmente DEPOIS do auto (cada jogo conta 1x)
 *       · somente-vazios: total de jogos manuais (preenchidos antes do auto
 *         + editados depois, sem duplicar o mesmo jogo)
 */
import type { Placar, PlacaresMap } from './copaCalculo';
import { placarValido } from './copaCalculo';
import { JOGOS_COPA_2026 } from '../data/copaMundo2026';

export type AutoUsado = 'sobrescreve' | 'somente-vazios' | null;

export interface MetaPreenchimento {
  autoUsado: AutoUsado;
  qtdManualAposAuto: number;
  /** ISO opcional para registro local. Não é enviado ao servidor. */
  timestampAuto: string;
}

export const META_VAZIA: MetaPreenchimento = {
  autoUsado: null,
  qtdManualAposAuto: 0,
  timestampAuto: '',
};

export const META_JOGO_ID = '__meta_auto__';

export function metaParaPlacar(meta: MetaPreenchimento): Placar | null {
  if (!meta || !meta.autoUsado) return null;
  return {
    mandante: meta.autoUsado === 'sobrescreve' ? 1 : 2,
    visitante: Math.min(99, Math.max(0, meta.qtdManualAposAuto || 0)),
  };
}

export function placarParaMeta(p: Placar | null | undefined): MetaPreenchimento {
  if (!p) return { ...META_VAZIA };
  const m = Number(p.mandante) || 0;
  const v = Number(p.visitante) || 0;
  if (m !== 1 && m !== 2) return { ...META_VAZIA };
  return {
    autoUsado: m === 1 ? 'sobrescreve' : 'somente-vazios',
    qtdManualAposAuto: v,
    timestampAuto: '',
  };
}

/** Extrai a meta de um mapa de palpites (procura o jogoId reservado). */
export function extrairMetaDosPalpites(
  palpites: Record<string, Placar | undefined> | undefined | null
): MetaPreenchimento {
  if (!palpites) return { ...META_VAZIA };
  const p = palpites[META_JOGO_ID];
  return placarParaMeta(p);
}

/**
 * Conta jogos manuais comparando palpite atual com snapshot do auto.
 * Usado no envio e na validação da meta gravada.
 */
export function contarJogosManuaisPorDiff(
  palpites: PlacaresMap,
  autoSnapshot: PlacaresMap,
  jogosManuaisAntesAuto: string[] = []
): number {
  const manuais = new Set<string>(jogosManuaisAntesAuto);
  JOGOS_COPA_2026.forEach((j) => {
    const jogoId = j.id;
    const atual = palpites[jogoId];
    if (!placarValido(atual)) return;
    const auto = autoSnapshot[jogoId];
    if (!placarValido(auto)) {
      manuais.add(jogoId);
      return;
    }
    if (atual.mandante !== auto.mandante || atual.visitante !== auto.visitante) {
      manuais.add(jogoId);
    }
  });
  return [...manuais].filter((id) => placarValido(palpites[id])).length;
}

/** Monta meta para envio — só inclui auto se houve auto confirmado nesta sessão ou reenvio legítimo. */
export function montarMetaPalpiteEnvio(opts: {
  metaAtual: MetaPreenchimento;
  palpites: PlacaresMap;
  autoSnapshot: PlacaresMap;
  jogosManuaisAntesAuto: string[];
  autoConfirmadoNestaSessao: boolean;
  reenvioComMetaAnterior: boolean;
}): MetaPreenchimento {
  const {
    metaAtual,
    palpites,
    autoSnapshot,
    jogosManuaisAntesAuto,
    autoConfirmadoNestaSessao,
    reenvioComMetaAnterior,
  } = opts;

  if (!metaAtual.autoUsado) return { ...META_VAZIA };

  const temSnapshot = Object.keys(autoSnapshot).some((k) =>
    placarValido(autoSnapshot[k])
  );

  if (!autoConfirmadoNestaSessao && !reenvioComMetaAnterior) {
    return { ...META_VAZIA };
  }

  if (!temSnapshot) {
    return autoConfirmadoNestaSessao || reenvioComMetaAnterior
      ? metaAtual
      : { ...META_VAZIA };
  }

  const qtdManual = contarJogosManuaisPorDiff(
    palpites,
    autoSnapshot,
    jogosManuaisAntesAuto
  );
  return { ...metaAtual, qtdManualAposAuto: qtdManual };
}

/** Texto curto pra exibir no badge do admin (legacy — substituído por badge unificado). */
export function rotuloAuto(meta: MetaPreenchimento): string {
  if (!meta || !meta.autoUsado) return '';
  if (meta.autoUsado === 'sobrescreve') {
    return meta.qtdManualAposAuto > 0
      ? `auto+${meta.qtdManualAposAuto} manual`
      : 'tudo automático';
  }
  return meta.qtdManualAposAuto > 0
    ? `auto vazios +${meta.qtdManualAposAuto} manual`
    : 'auto só vazios';
}
